/**
 * Canonical botanical data model (spec §8). This is plain domain data — never a Three.js object.
 * See `docs/FLORA_SCHEMA.md`. Catalog records ship as versioned JSON validated by
 * `FloraValidation.ts` before use.
 *
 * There is exactly **one** record per plant, for every site in every climate. A definition describes
 * the plant's whole supported range — the zones it survives, the season it needs, the regions it is
 * native to, the phenology it runs on — and `localization/FloraResolver.ts` reads a site-specific
 * answer out of it at runtime. Nothing in this file is ever rewritten because a site was localized;
 * see `docs/SITE_LOCALIZATION.md`.
 */

import type { FloraEcologyDefinition } from './FloraEcology';

export type FloraCategory =
  | 'deciduous-tree'
  | 'evergreen-tree'
  | 'deciduous-shrub'
  | 'evergreen-shrub'
  | 'ornamental-grass'
  | 'perennial'
  | 'annual'
  | 'groundcover';

export const FLORA_CATEGORIES: readonly FloraCategory[] = [
  'deciduous-tree',
  'evergreen-tree',
  'deciduous-shrub',
  'evergreen-shrub',
  'ornamental-grass',
  'perennial',
  'annual',
  'groundcover',
];

export type SunlightRequirement = 'full-sun' | 'part-sun' | 'part-shade' | 'full-shade';
export type MoistureRequirement = 'dry' | 'average' | 'moist' | 'wet';

export type ContainerStageId =
  | '1gal'
  | '2gal'
  | '3gal'
  | '5gal'
  | '7gal'
  | '15gal'
  | 'field'
  | 'mature';

export type GrowthCurve = 'linear' | 'ease-out' | 'logistic';
export type TrunkCurve = 'linear' | 'allometric';
export type DensityCurve = 'linear' | 'ease-in' | 'ease-out';

export interface ContainerStage {
  id: ContainerStageId;
  approximateAgeYears: number;
  heightMeters: number;
  spreadMeters: number;
}

export interface DimensionRange {
  minimum: number;
  typical: number;
  maximum: number;
}

export interface FloraGrowthDefinition {
  containerStages: ContainerStage[];
  matureHeightMeters: DimensionRange;
  matureSpreadMeters: DimensionRange;
  annualGrowthRateMeters?: number;
  heightCurve: GrowthCurve;
  spreadCurve: GrowthCurve;
  trunkCurve?: TrunkCurve;
  densityCurve?: DensityCurve;
}

export interface SeasonalAppearance {
  foliageColor: string;
  foliageDensity: number;
  flowerColor?: string;
  flowerDensity?: number;
  fruitColor?: string;
  fruitDensity?: number;
  branchVisibility: number;
  dormant: boolean;
}

export interface BloomWindow {
  startDayOfYear: number;
  endDayOfYear: number;
  intensity: number;
  color: string;
}

export interface FloraSeasonDefinition {
  evergreen: boolean;
  spring: SeasonalAppearance;
  summer: SeasonalAppearance;
  autumn: SeasonalAppearance;
  winter: SeasonalAppearance;
  bloomWindows?: BloomWindow[];
  leafOutDay?: number;
  autumnColorStartDay?: number;
  leafDropDay?: number;
}

/** Procedural geometry archetype used by `FloraGeometry`/`FloraFactory`. */
export type FloraGeometryForm =
  | 'round-canopy-tree'
  | 'conical-tree'
  | 'mounding-shrub'
  | 'grass-tuft'
  | 'perennial-clump'
  | 'groundcover-mat';

export interface FloraRenderingDefinition {
  geometryForm: FloraGeometryForm;
  trunkColor?: string;
  /** LOD swap distances (meters): [medium, low]. Beyond low → impostor. */
  lodDistancesMeters: [number, number];
  /** Distance beyond which an impostor/billboard is used. */
  impostorDistanceMeters: number;
}

export interface FloraPlacementDefinition {
  /** Footprint radius at maturity (meters) for validity/overlap checks. */
  footprintRadiusMeters: number;
  /** Recommended center-to-center spacing at maturity (meters). */
  recommendedSpacingMeters: number;
  /** Maximum slope (degrees) the plant tolerates before a warning. */
  maxSlopeDegrees: number;
}

/* ------------------------------- Climatic range -------------------------------- */

/**
 * The span of climates this plant will actually grow in.
 *
 * The whole localization design rests on this block. A flora record describes a **range**, and a site
 * supplies a **point** inside or outside it; the resolver compares the two. That is why there is one
 * canonical Red Oak rather than one per zone — the oak's record says "zones 3 through 9, tolerates
 * heat to AHS 8, needs 120 frost-free days", and every site reads its own answer out of that.
 *
 * `hardinessZones` on the record predates this block and remains the authority on cold. Everything
 * here is the other half of the question: heat, season length, and how much slack the plant has.
 */
export interface FloraClimateDefinition {
  /**
   * Coldest USDA zone value the plant survives, as a zone value (6.5 = 6b). Falls back to
   * `hardinessZones.minimum` when absent — the two say the same thing, at different precision.
   */
  minimumZone?: number;
  /** Warmest zone value, above which winters are too mild (chill requirement, disease, decline). */
  maximumZone?: number;
  /**
   * Highest AHS heat zone tolerated — average annual days above 30 °C, banded 1–12. Absent means the
   * record has no opinion, and no heat warning is raised rather than one being guessed at.
   */
  maximumHeatZone?: number;
  /** Frost-free days needed to complete a season's growth, flower, and harden off. */
  minimumGrowingSeasonDays?: number;
  /**
   * How far outside its stated zone range the plant can be pushed before the warning becomes a
   * refusal, in zone values. A tough oak has slack; a marginal broadleaf evergreen has none.
   */
  zoneToleranceSlack?: number;
  /** True where established plants shrug off drought — softens a dry-site moisture warning. */
  droughtTolerant?: boolean;
  /** True where the plant needs consistently moist ground and suffers without it. */
  moistureSensitive?: boolean;
}

/* --------------------------------- Phenology ----------------------------------- */

/**
 * How this plant's calendar responds to being moved somewhere else.
 *
 * The `seasons` block already carries baseline days — leaf-out 100, autumn colour 280 — and this
 * block says what those days *mean*: the climate they were measured in, and how much of the shift
 * between climates the plant actually follows.
 *
 * The responsiveness figures are the honest part. Not everything tracks temperature: a witch hazel
 * blooms on warmth and moves weeks between Georgia and Vermont, while a plant triggered by day
 * length barely moves at all. One number per end of the year keeps that distinction in the data
 * instead of hard-coding "everything shifts by the same amount", and leaves room for a real Growing
 * Degree Day model to replace the linear approximation without the field changing meaning.
 */
export interface FloraPhenologyDefinition {
  /**
   * Zone value the baseline days in `seasons` were authored for. Defaults to the catalog's reference
   * climate (zone 6b) when absent — see `localization/SiteLocalization.ts`.
   */
  referenceZone?: number;
  /**
   * 0–1. How much of a site's shift in last-frost date this plant's spring events follow.
   * 1 = fully thermal (moves with the frost date), 0 = photoperiod-driven (does not move).
   */
  springResponsiveness?: number;
  /** The same for autumn colour, leaf drop, and dormancy against the first-frost date. */
  autumnResponsiveness?: number;
  /** Days from the end of bloom to ripe fruit. */
  fruitMaturationDays?: number;
  /** Days ripe fruit stays on the plant before it is eaten or drops. */
  fruitPersistenceDays?: number;
  /** Days from bloom's end to seed set. Defaults to `fruitMaturationDays` when absent. */
  seedMaturationDays?: number;
  /** Days seed remains available on standing stems or in cones. */
  seedPersistenceDays?: number;
  /**
   * Day of year dormancy is fully established, when it differs from leaf drop. Evergreens and
   * grasses drop no leaves but still stop growing.
   */
  dormancyDay?: number;
}

/* ------------------------------- Native range ---------------------------------- */

/**
 * Where this plant belongs — as a set of regions, never as a boolean.
 *
 * `native: true` is the field this block exists to replace. *Cercis canadensis* is native across the
 * eastern US and an import in Oregon; *Pachysandra terminalis* is native to nothing in North America
 * and naturalizes aggressively in parts of it. A single flag cannot say either thing, and a catalog
 * that ships one is wrong for every site that disagrees with whoever wrote it.
 *
 * The region codes are `NativeRegionCode` from `localization/SiteLocalization.ts`, kept as plain
 * strings here so the flora schema does not depend on the localization module.
 */
export interface FloraNativeRangeDefinition {
  /** Regions the plant is indigenous to. */
  native?: string[];
  /** Regions where it grows well and is not indigenous — the ordinary garden-plant case. */
  introduced?: string[];
  /** Regions where it escapes cultivation and is a documented problem. */
  invasive?: string[];
  /** Where it comes from, in words: `Eastern North America`, `Japan and Korea`. */
  origin?: string;
}

export interface FloraDefinition {
  id: string;
  schemaVersion: number;
  botanicalName: string;
  commonNames: string[];
  cultivar?: string;
  category: FloraCategory;
  tags: string[];
  hardinessZones?: { minimum: number; maximum: number };
  sunlight: SunlightRequirement[];
  moisture: MoistureRequirement[];
  growth: FloraGrowthDefinition;
  seasons: FloraSeasonDefinition;
  rendering: FloraRenderingDefinition;
  placement: FloraPlacementDefinition;
  /**
   * Schema 2 additions, every one optional.
   *
   * A record that omits all four is exactly the record schema 1 described, and resolves against an
   * unlocalized site to the behaviour the app had before localization existed. That is what lets the
   * catalog be enriched a species at a time instead of all at once.
   */
  climate?: FloraClimateDefinition;
  phenology?: FloraPhenologyDefinition;
  nativeRange?: FloraNativeRangeDefinition;
  ecology?: FloraEcologyDefinition;
}

/**
 * Current flora schema version understood by this build.
 *
 * Bumped to 2 when records gained climatic range, localization phenology, native range and ecology.
 * A version-1 record still loads unchanged: the four blocks are optional, and their absence means
 * "not yet described" rather than "described as empty".
 */
export const FLORA_SCHEMA_VERSION = 2;
